import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Settings, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { apiFetch } from '../hooks/useFetch';

const INITIAL_VISIBLE = 10;

const ACCENT_LABELS = {
  natural: 'Natural (Katie)',
  british: 'British accent cue',
  irish: 'Irish accent cue',
  scottish: 'Scottish accent cue',
  welsh: 'Welsh accent cue',
  australian: 'Australian accent cue',
  newzealand: 'NZ accent cue',
  african: 'African accent cue',
  southafrican: 'South African accent cue',
  indian: 'Indian English accent cue',
  caribbean: 'Caribbean accent cue',
  jamaican: 'Jamaican accent cue',
  spanish: 'Spanish (language code)',
};

export const PronunciationMappingCard = () => {
  const [data, setData] = useState({ mappings: {}, available_accents: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  // Memoised: sort the draft mapping by category name once per change so
  // we don't run Object.entries + sort on every render of the dialog.
  const sortedDraftEntries = useMemo(
    () => Object.entries(draft).sort(([a], [b]) => a.localeCompare(b)),
    [draft],
  );

  const load = async () => {
    setLoading(true);
    const { data: resp, error } = await apiFetch('/api/voice/accent-categories');
    if (!error && resp) {
      setData(resp);
      setDraft(resp.mappings || {});
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const sortedEntries = Object.entries(data.mappings || {}).sort(([a], [b]) => a.localeCompare(b));
  const visible = expanded ? sortedEntries : sortedEntries.slice(0, INITIAL_VISIBLE);
  const hidden = sortedEntries.length - visible.length;

  const handleSaveAll = async () => {
    setSaving(true);
    let failures = 0;
    for (const [category, accent] of Object.entries(draft)) {
      if ((data.mappings || {})[category] !== accent) {
        const { error } = await apiFetch('/api/voice/accent-categories/update', {
          method: 'POST',
          body: { category, accent },
        });
        if (error) failures += 1;
      }
    }
    setSaving(false);
    if (failures === 0) {
      toast.success('Pronunciation mappings updated');
      setEditOpen(false);
      load();
    } else {
      toast.error(`${failures} mapping(s) failed to save`);
    }
  };

  const accentOptions = (data.available_accents && data.available_accents.length
    ? data.available_accents
    : Object.keys(ACCENT_LABELS)
  );

  return (
    <Card data-testid="pronunciation-mapping-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings className="h-5 w-5 text-[#00E5CC]" />
              Pronunciation Mapping
            </CardTitle>
            <CardDescription className="mt-1">
              Single voice (Katie Natural) with per-category accent cues. Spanish uses the
              ElevenLabs language code; other regions use a short bracketed performance cue.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setDraft(data.mappings || {}); setEditOpen(true); }}
            data-testid="edit-mappings-btn"
          >
            Edit
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading mappings…
          </div>
        ) : sortedEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No mappings configured.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {visible.map(([category, accent]) => (
                <div
                  key={category}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-muted/40 text-sm"
                  data-testid={`mapping-${category}`}
                >
                  <span className="font-medium capitalize truncate" title={category}>
                    {category}
                  </span>
                  <span className="text-xs text-muted-foreground capitalize shrink-0">
                    {ACCENT_LABELS[accent] || accent || 'natural'}
                  </span>
                </div>
              ))}
            </div>

            {sortedEntries.length > INITIAL_VISIBLE && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-3 w-full text-[#00E5CC] hover:text-[#00C9B3]"
                onClick={() => setExpanded(v => !v)}
                data-testid="toggle-more-mappings-btn"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    +{hidden} more
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-mappings-dialog">
          <DialogHeader>
            <DialogTitle>Edit Pronunciation Mappings</DialogTitle>
            <CardDescription>
              All audio uses Katie&apos;s Natural Voice. Spanish categories add a Spanish
              language hint; other accents add a bracketed style cue to the prompt.
            </CardDescription>
          </DialogHeader>
          <div className="space-y-2">
            {sortedDraftEntries.map(([cat, accent]) => (
              <div key={cat} className="flex items-center gap-2">
                <span className="flex-1 text-sm font-medium capitalize" title={cat}>{cat}</span>
                <Select
                  value={accent || 'natural'}
                  onValueChange={(val) => setDraft(prev => ({ ...prev, [cat]: val }))}
                >
                  <SelectTrigger className="w-44" data-testid={`select-accent-${cat}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accentOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{ACCENT_LABELS[opt] || opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]"
              data-testid="save-mappings-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save mappings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PronunciationMappingCard;
